import type { SVGProps, ComponentType } from "react";
import * as Icons from "@/components/icons";
import { getProductById, type ProductId } from "@/lib/products";

// Type alias replacing react-icons' IconType. Our local components are plain
// SVGElement props receivers, with the same call signature.
type IconType = ComponentType<SVGProps<SVGSVGElement>>;

// The local icons module exports every icon we use, statically. This is the
// authoritative iconMap — no react-icons runtime dep, fully tree-shakeable.
const iconMap: Record<string, IconType> = Icons as Record<string, IconType>;

interface DynamicIconProps {
  name: string;
  productId?: ProductId; // Optional product ID to look up icon color from product data
  className?: string; // Optional className for background icons or custom sizing
  useDefaultColors?: boolean; // If true, skip color lookup and inherit from parent (for products page)
}

export default function DynamicIcon({
  name,
  productId,
  className,
  useDefaultColors,
}: DynamicIconProps) {
  const IconComponent = iconMap[name];

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found in DynamicIcon map.`);
    // Fallback icon - inherit color from parent
    return <Icons.HiSparkles className={className || "w-6 h-6"} />;
  }

  // Get icon color from product data if productId is provided and useDefaultColors is false
  // If useDefaultColors is true, don't set style - let it inherit from parent (default behavior)
  let iconStyle: React.CSSProperties | undefined;
  if (!useDefaultColors && productId) {
    const product = getProductById(productId);
    const iconColor = product?.icon?.color;
    if (iconColor) {
      iconStyle = { color: iconColor };
    }
  }

  return <IconComponent className={className || "w-6 h-6"} style={iconStyle} />;
}
